import { useState, useMemo, useEffect, useRef } from 'react'
import './MissionStagesDropdown.css'
import { playSound } from '../../utils/soundFX'

export function MissionStagesDropdown({ missionStages, artemisProgress, mediaQueryKey, onExpandedChange }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const dropdownRef = useRef(null)

  const isMounted = useRef(false)

  const toggleDropdown = (event) => {
    event.stopPropagation()
    setIsExpanded(!isExpanded)
  }
  
  // Notify parent when expanded state changes & play open/close sounds
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    playSound(isExpanded ? 'MissionObjective_Open' : 'MissionObjective_Close')
    if (onExpandedChange) {
      onExpandedChange(isExpanded)
    }
  }, [isExpanded, onExpandedChange])

  // Handle click/tap on dropdown to toggle it
  const handleDropdownClick = (event) => {
    // If clicking on header, let the header's onClick handle it (toggle)
    if (event.target.closest('.mission-stages-dropdown-header')) {
      return
    }
    // Clicking anywhere else on the dropdown toggles the expanded state
    setIsExpanded(!isExpanded)
  }

  // Find the latest completed stage
  const latestCompletedStage = useMemo(() => {
    const visibleStages = missionStages.filter(stage => stage.visible)
    // Find the highest stage that has been reached
    const completedStages = visibleStages.filter(stage => artemisProgress >= stage.trajectoryPercent)
    return completedStages.length > 0 ? completedStages[completedStages.length - 1] : null
  }, [missionStages, artemisProgress])

  return (
    <div ref={dropdownRef} className={`mission-stages-dropdown ${isExpanded ? 'expanded' : 'collapsed'}`} onClick={handleDropdownClick}>
      {/* Dropdown header - always visible */}
      <div className="mission-stages-dropdown-header" onClick={toggleDropdown}>
        <span className="mission-stages-dropdown-title">MISSION STAGES</span>
        <span className="mission-stages-dropdown-arrow">{isExpanded ? '<<<' : '>>>'}</span>
      </div>
      
      {/* Latest completed stage - shown when collapsed */}
      {!isExpanded && latestCompletedStage && (
        <div className="mission-stages-dropdown-preview">
          <div className="mission-stage-item reached">
            <span className="mission-stage-number">
              {(missionStages.filter(s => s.visible).findIndex(s => s.id === latestCompletedStage.id) + 1).toString().padStart(2, '0')}
            </span>
            <span className="mission-stage-connector reached"></span>
            <span className="mission-stage-name">{latestCompletedStage.shortName || latestCompletedStage.name}</span>
          </div>
        </div>
      )}

      {/* Dropdown content - shows/hides based on state */}
      <div className="mission-stages-dropdown-content">
        {/* Mobile-only close button — position:fixed works here (no transform on this element) */}
        <button
          className="mission-stages-dropdown-close-mobile"
          onClick={(e) => { e.stopPropagation(); setIsExpanded(false) }}
        >
          ×
        </button>
        <div className="mission-stages-divider" />
        
        {/* Stages list */}
        {missionStages
          .filter(stage => stage.visible)
          .map((stage, index) => {
            const reached = artemisProgress >= stage.trajectoryPercent
            const stageNumber = (index + 1).toString().padStart(2, '0')
            const isDesktop = mediaQueryKey === 'desktopStandard' || mediaQueryKey === 'desktopLarge' || mediaQueryKey === 'desktop4k'
            const isLargeDesktop = mediaQueryKey === 'desktopLarge' || mediaQueryKey === 'desktop4k'
            const displayName = isLargeDesktop ? stage.name : (stage.shortName || stage.name)
            
            return (
              <div
                key={stage.id}
                className={`mission-stage-item ${reached ? 'reached' : 'pending'}`}
              >
                <span className="mission-stage-number">{stageNumber}</span>
                <span className={`mission-stage-connector ${reached ? 'reached' : 'pending'}`}></span>
                <span className="mission-stage-name">{displayName}{reached && isDesktop ? ' /// COMPLETE ///' : ''}</span>
              </div>
            )
          })}
      </div>
    </div>
  )
}